import { memo, useMemo } from "react";
import ReactCurvedText from "react-curved-text";
import { useMediaQuery } from "react-responsive";

interface Props {
  size: number; // same as width/height of image
  text: string;
  stroke?: string; // stroke color
}

const CurvedTextOverlay = memo(function CurvedTextOverlay({
  size,
  text,
  stroke,
}: Props) {
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLgTablet = useMediaQuery({ minWidth: 992, maxWidth: 1023 });
  const isMdTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });

  const { width, height, fontSize, cx, cy, rx, ry, startOffset } = useMemo(() => {
    const w = size * 1.5;
    const h = size * 1.35;

    const fs = isDesktop ? 18 : isLgTablet ? 14 : isMdTablet ? 13 : 9;
    const centerX = isDesktop
      ? w / 2.3
      : isLgTablet
      ? w / 2.25
      : isMdTablet
      ? w / 2.1
      : w / 2.2;
    const centerY = isDesktop
      ? h / 2.5
      : isLgTablet
      ? h / 2.4
      : isMdTablet
      ? h / 2.4
      : h / 2.38;
    const radiusX = isDesktop
      ? size * 0.46
      : isLgTablet
      ? size * 0.45
      : isMdTablet
      ? size * 0.43
      : size * 0.42;
    const radiusY = isDesktop
      ? size * 0.68
      : isLgTablet
      ? size * 0.66
      : isMdTablet
      ? size * 0.64
      : size * 0.62;
    const offset = isDesktop ? 35 : isLgTablet ? 34 : isMdTablet ? 32 : 30;

    return {
      width: w,
      height: h,
      fontSize: fs,
      cx: centerX,
      cy: centerY,
      rx: radiusX,
      ry: radiusY,
      startOffset: offset,
    };
  }, [size, isDesktop, isLgTablet, isMdTablet]);

  const textProps = useMemo(
    () => ({
      style: {
        fontSize,
        fill: "white",
        stroke,
        strokeWidth: fontSize * 0.4,
        paintOrder: "stroke" as const,
        letterSpacing: "0.15em",
        strokeLinejoin: "round" as const,
        strokeLinecap: "round" as const,
        direction: "ltr" as const,
      },
    }),
    [fontSize, stroke]
  );

  return (
    <div dir="ltr" style={{ direction: "ltr", unicodeBidi: "isolate" }}>
      <ReactCurvedText
        width={width}
        height={height}
        cx={cx}
        cy={cy}
        rx={rx}
        ry={ry}
        startOffset={startOffset}
        reversed={false}
        text={text}
        textProps={textProps}
      />
    </div>
  );
});

CurvedTextOverlay.displayName = "CurvedTextOverlay";

export default CurvedTextOverlay;
